import { BranchOutcomeRequest } from "../Shared/Generated/BranchOutcomeRequest";
import { BranchRecoveryDiagnostics } from "../Shared/Generated/BranchRecoveryDiagnostics";
import { BranchResolverRequestDiagnostic } from "../Shared/Generated/BranchResolverRequestDiagnostic";
import { BranchResolverRequestOutcome } from "../Shared/Generated/BranchResolverRequestOutcome";
import { BranchPageVisibility } from "../Shared/Generated/BranchPageVisibility";
import { BranchSdkErrorType } from "../Shared/Generated/BranchSdkErrorType";

type RequestDiagnostics = Pick<BranchRecoveryDiagnostics, "resolveRequests" | "resolveRequestCount" | "elapsedMs" | "sdkElapsedMs" | "wasHidden">;
function requestDiagnostics(value: unknown): RequestDiagnostics | undefined {
    if (!value || typeof value !== "object") return;
    const d = value as RequestDiagnostics;
    const duration = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 120000;
    const id = (v: unknown) => v === null || typeof v === "string" && /^[a-f0-9]{32}$/i.test(v);
    if (!duration(d.elapsedMs) || !(d.sdkElapsedMs === null || duration(d.sdkElapsedMs)) || typeof d.wasHidden !== "boolean" ||
        !Array.isArray(d.resolveRequests) || d.resolveRequests.length > 2 || d.resolveRequests.length !== d.resolveRequestCount) return;
    const requests: BranchResolverRequestDiagnostic[] = [];
    for (const [index, r] of d.resolveRequests.entries()) {
        if (!r || r.requestNumber !== index + 1 || !duration(r.startedAfterMs) || !duration(r.elapsedMs) ||
            !(r.headersMs === null || duration(r.headersMs) && r.headersMs <= r.elapsedMs) ||
            !(r.bodyMs === null || duration(r.bodyMs) && r.bodyMs <= r.elapsedMs) ||
            (r.headersMs ?? 0) + (r.bodyMs ?? 0) > r.elapsedMs + 1 ||
            !(r.httpStatus === null || Number.isInteger(r.httpStatus) && r.httpStatus >= 100 && r.httpStatus <= 599) ||
            !id(r.serverRequestId) || !id(r.correlationId) || typeof r.wasHidden !== "boolean" ||
            !Object.values(BranchResolverRequestOutcome).includes(r.outcome) || !Object.values(BranchSdkErrorType).includes(r.errorType) ||
            !Object.values(BranchPageVisibility).includes(r.visibilityAtStart) || !Object.values(BranchPageVisibility).includes(r.visibilityAtEnd)) return;
        requests.push({ requestNumber: r.requestNumber, startedAfterMs: r.startedAfterMs, elapsedMs: r.elapsedMs,
            headersMs: r.headersMs, bodyMs: r.bodyMs, httpStatus: r.httpStatus, outcome: r.outcome, errorType: r.errorType,
            serverRequestId: r.serverRequestId, correlationId: r.correlationId, wasHidden: r.wasHidden,
            visibilityAtStart: r.visibilityAtStart, visibilityAtEnd: r.visibilityAtEnd });
    }
    return { resolveRequests: requests, resolveRequestCount: requests.length, elapsedMs: d.elapsedMs, sdkElapsedMs: d.sdkElapsedMs, wasHidden: d.wasHidden };
}

export const pendingBranchOutcomeKey = "smartguide.branch.pending-outcome.v1";
const allowedArrivalClockSkewMs = 5000;
export interface PendingBranchOutcome {
    attemptId: string;
    link: string;
    correlationId: string | null;
    path: string;
    createdAt: number;
    requestDiagnostics?: RequestDiagnostics;
}

export function clearPendingBranchOutcome(): void {
    try { sessionStorage.removeItem(pendingBranchOutcomeKey); } catch { /* Optional telemetry cannot change routing. */ }
}

export function rememberBranchArrival(record: PendingBranchOutcome): void {
    try { sessionStorage.setItem(pendingBranchOutcomeKey, JSON.stringify({ ...record, requestDiagnostics: requestDiagnostics(record.requestDiagnostics) })); } catch { /* Navigation still proceeds without an arrival signal. */ }
}

export function takeBranchArrival(): PendingBranchOutcome | null {
    try {
        const raw = sessionStorage.getItem(pendingBranchOutcomeKey);
        clearPendingBranchOutcome();
        if (!raw || raw.length > 4096) return null;
        const value = JSON.parse(raw) as PendingBranchOutcome;
        const ageMs = Date.now() - value?.createdAt;
        if (!value || !/^[a-f0-9]{32}$/i.test(value.attemptId) ||
            typeof value.link !== "string" || !/^https:\/\/[a-z0-9.-]+\/[a-z0-9_-]+(?:\/[a-z0-9_-]+)*$/i.test(value.link) ||
            value.link.length > 512 || typeof value.path !== "string" || value.path.length > 1024 ||
            !value.path.startsWith("/destinations/") || /[?#\\\u0000-\u0020]/.test(value.path) ||
            value.correlationId !== null && (typeof value.correlationId !== "string" || !/^[a-f0-9]{32}$/i.test(value.correlationId)) ||
            !Number.isFinite(value.createdAt) || ageMs < -allowedArrivalClockSkewMs || ageMs > 120000) return null;
        return { attemptId: value.attemptId, link: value.link, correlationId: value.correlationId, path: value.path, createdAt: value.createdAt,
            requestDiagnostics: requestDiagnostics(value.requestDiagnostics) };
    } catch { return null; }
}

export async function reportBranchOutcome(report: BranchOutcomeRequest, csrf?: string): Promise<void> {
    try {
        if (!csrf) {
            const response = await fetch("/branch/outcome", { credentials: "same-origin", cache: "no-store" });
            if (!response.ok) return;
            const body = await response.json();
            if (typeof body?.token !== "string" || body.token.length > 2048) return;
            csrf = body.token;
        }
        await fetch("/branch/outcome", {
            method: "POST", credentials: "same-origin", keepalive: true,
            headers: { "Content-Type": "application/json", RequestVerificationToken: csrf! },
            body: JSON.stringify(report)
        });
    } catch { /* Never interrupt navigation or recursively report a telemetry failure. */ }
}
